/**
 * La saisie des heures d'un technicien pour un jour d'une intervention (lot U2, ADR-0016) : le bureau rattrape des heures
 * oubliées. Le technicien est ajouté à l'équipe de ce jour s'il n'en fait pas partie ; on ne saisit pas d'heures pour un jour à
 * venir ; une saisie vide efface. Ce sont les heures SAISIES : le palier de facturation (quinze minutes supérieur, par
 * technicien et par jour) se calcule à la validation, jamais ici.
 *
 * Le refus d'un geste a son propre état (jamais effacé par la relecture qui suit) ; un conflit de révision relit l'écran PUIS le dit.
 */
import { useEffect, useState } from 'react';

import { ApiError, api, type BriefingIntervention, type Technicien } from '../api/client';
import { aujourdhuiLocal } from '../domaine/interventions';
import { jourFr, jourValide } from '../domaine/planning';
import { jourAvecHeuresPossibles, jourPropose, lireHeuresSaisies, phraseHeuresEnregistrees } from '../domaine/planning-interventions';

export function SaisieHeuresIntervention({
  interventionId,
  revision,
  jours,
  onChange,
  onMessage,
}: {
  interventionId: string;
  revision: number;
  jours: BriefingIntervention['jours'];
  /** Une écriture a réussi (ou un conflit) : la page relit la semaine, la liste et le briefing. */
  onChange: () => void;
  onMessage: (message: string) => void;
}) {
  const aujourdhui = aujourdhuiLocal();
  const [ouvert, setOuvert] = useState(false);
  const [techniciens, setTechniciens] = useState<readonly Technicien[]>([]);
  const [jour, setJour] = useState(() => jourPropose(jours, aujourdhui));
  const [technicianId, setTechnicianId] = useState('');
  const [duree, setDuree] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!ouvert) return;
    let annule = false;
    api
      .techniciensPlanning(false)
      .then((r) => {
        if (!annule) setTechniciens(r.items.filter((t) => !t.archived));
      })
      .catch(() => {
        if (!annule) setErreur('La liste des techniciens n’a pas pu être lue.');
      });
    return () => {
      annule = true;
    };
  }, [ouvert]);

  const enregistrer = async () => {
    setErreur(null);
    const minutes = lireHeuresSaisies(duree);
    if (minutes === 'ERREUR') {
      setErreur('Écrivez une durée comme 3, 7,5, 1h30 ou 45 min (de 1 minute à 24 heures). Laissez vide pour effacer.');
      return;
    }
    if (!jourValide(jour)) {
      setErreur('Choisissez un jour valide.');
      return;
    }
    if (minutes !== null && !jourAvecHeuresPossibles(jour, aujourdhui)) {
      setErreur('On ne saisit pas d’heures pour un jour à venir : choisissez aujourd’hui ou un jour passé.');
      return;
    }
    const technicien = techniciens.find((t) => t.id === technicianId);
    if (!technicien) {
      setErreur('Choisissez le technicien.');
      return;
    }
    setEnCours(true);
    try {
      await api.saisirHeuresIntervention(interventionId, revision, jour, technicien.id, minutes);
      setDuree('');
      onMessage(phraseHeuresEnregistrees(technicien.name, jour, minutes));
      onChange();
    } catch (e) {
      // 409 : l'intervention a changé depuis l'affichage. On relit, PUIS on le dit.
      if (e instanceof ApiError && e.status === 409) onChange();
      setErreur(e instanceof ApiError || e instanceof Error ? e.message : 'Saisie impossible.');
    } finally {
      setEnCours(false);
    }
  };

  if (!ouvert) {
    return (
      <div className="saisie-ligne">
        <button type="button" className="secondaire" onClick={() => setOuvert(true)}>
          Saisir des heures
        </button>
      </div>
    );
  }

  return (
    <div className="carte saisie-heures">
      <p className="aide">
        Heures réellement travaillées par un technicien un jour donné. S’il n’est pas dans l’équipe de ce jour, il y est ajouté. La
        facturation se fait par palier de quinze minutes supérieur, technicien par technicien, à la validation. Un technicien qui a pointé ce jour-là a des heures calculées : on corrige alors un de ses pointages (carte « Pointages »).
      </p>
      <div className="saisie-ligne">
        <label htmlFor="heures-jour">Jour</label>
        <input id="heures-jour" type="date" value={jour} max={aujourdhui} onChange={(e) => setJour(e.target.value)} />
        <label htmlFor="heures-technicien">Technicien</label>
        <select id="heures-technicien" value={technicianId} onChange={(e) => setTechnicianId(e.target.value)}>
          <option value="">Choisir…</option>
          {techniciens.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <label htmlFor="heures-duree">Durée</label>
        <input
          id="heures-duree"
          type="text"
          inputMode="decimal"
          placeholder="3, 7,5, 1h30, 45 min"
          value={duree}
          onChange={(e) => setDuree(e.target.value)}
          aria-describedby="heures-aide"
        />
        <button type="button" disabled={enCours} onClick={() => void enregistrer()}>
          Enregistrer les heures
        </button>
        <button type="button" className="secondaire" disabled={enCours} onClick={() => setOuvert(false)}>
          Fermer
        </button>
      </div>
      <p id="heures-aide" className="aide">
        {jourValide(jour) ? `Pour le ${jourFr(jour)}. ` : ''}Durée vide : efface les heures saisies de ce technicien ce jour-là.
      </p>
      {erreur && <div className="message erreur">{erreur}</div>}
    </div>
  );
}
